import { useCallback, useState } from 'react';

/**
 * Valores distintos de un campo de texto, leídos de las parcelas cargadas en la
 * vista. Alimenta desplegables (hoy: Barrio) sin catálogo de backend, con la
 * ventaja de que el valor ofrecido es exactamente el que compara el filtro.
 *
 * Se recalcula on-demand (al abrir el desplegable), no en cada movimiento del
 * mapa: recorrer las features cargadas es barato pero no gratis.
 */

/** Tope de features recorridas por lectura: un viewport de polígonos puede traer decenas de miles. */
const MAX_FEATURES_SCANNED = 40000;

/** Tope de opciones ofrecidas; con búsqueda, más que esto no aporta. */
const MAX_VALUES = 300;

/**
 * @param {object} options
 * @param {{current: object|null}} options.mapInstanceRef
 * @param {(map: object) => Array<{sourceId: string, sourceLayer: string}>} options.getActiveParcelSources
 * @param {string} options.field prop del tile a leer
 * @returns {{values: string[], refreshValues: () => void}}
 */
export function useParcelTileValues({ mapInstanceRef, getActiveParcelSources, field }) {
  const [values, setValues] = useState([]);

  const refreshValues = useCallback(() => {
    const map = mapInstanceRef?.current;
    if (!map || !map.style) return;

    const sources = getActiveParcelSources?.(map) || [];
    if (sources.length === 0) {
      setValues([]);
      return;
    }

    const found = new Set();
    let scanned = 0;

    for (const { sourceId, sourceLayer } of sources) {
      if (scanned >= MAX_FEATURES_SCANNED || found.size >= MAX_VALUES) break;

      let features = [];
      try {
        features = map.querySourceFeatures(sourceId, { sourceLayer }) || [];
      } catch {
        // El source puede estar recargándose (firma vencida): se ignora esta lectura.
        features = [];
      }

      for (const feature of features) {
        scanned += 1;
        if (scanned >= MAX_FEATURES_SCANNED || found.size >= MAX_VALUES) break;
        const raw = feature?.properties?.[field];
        if (typeof raw !== 'string') continue;
        const value = raw.trim();
        if (value.length > 0) found.add(value);
      }
    }

    setValues(Array.from(found).sort((a, b) => a.localeCompare(b, 'es')));
  }, [mapInstanceRef, getActiveParcelSources, field]);

  return { values, refreshValues };
}
